import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Inject,
  RowSelectEventArgs,
  SortDirection,
} from '@syncfusion/ej2-react-grids';
import {
  AccountTree,
  Visibility as ViewIcon,
  PlayArrow as ActivateIcon,
  Pause as DeactivateIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { WorkflowListItem } from '../../../../services/WorkflowService';
import { LoadingIndicator } from '../../../common/components/styled-ui-elements';
import { WorkflowStatusChip, WorkflowTriggerBadge } from '../shared';

interface WorkflowsGridProps {
  workflows: WorkflowListItem[];
  onView: (id: string) => void;
  onActivateToggle: (id: string, isActive: boolean) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

const WorkflowsGrid: React.FC<WorkflowsGridProps> = ({
  workflows,
  onView,
  onActivateToggle,
  onDelete,
  isLoading = false,
}) => {
  const handleRowSelect = (args: RowSelectEventArgs) => {
    const selectedWorkflow = args.data as WorkflowListItem;
    onView(selectedWorkflow.id);
  };

  const triggerTemplate = (props: WorkflowListItem) => (
    <WorkflowTriggerBadge trigger={props.trigger} />
  );

  const statusTemplate = (props: WorkflowListItem) => (
    <WorkflowStatusChip isActive={props.isActive} />
  );

  const dateTemplate = (props: WorkflowListItem) => (
    <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
      {new Date(props.createdDate).toLocaleDateString()}
    </Typography>
  );

  const actionsTemplate = (props: WorkflowListItem) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="View Details">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onView(props.id); }}
          sx={{ color: '#3b82f6' }}
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={props.isActive ? 'Deactivate' : 'Activate'}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onActivateToggle(props.id, props.isActive); }}
          sx={{ color: props.isActive ? '#f59e0b' : '#10b981' }}
        >
          {props.isActive ? <DeactivateIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(props.id); }}
          sx={{ color: '#ef4444' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading) {
    return <LoadingIndicator icon={AccountTree} message="Loading workflows..." />;
  }

  if (!workflows || workflows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography variant="body1" color="textSecondary">No workflows found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: '#64748b', fontStyle: 'italic' }}>
        Double-click on any row to view workflow details
      </Typography>
      <GridComponent
        dataSource={workflows}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 10, pageSizes: [10, 25, 50, 100] }}
        sortSettings={{ columns: [{ field: 'createdDate', direction: 'Descending' as SortDirection }] }}
        height={600}
        gridLines="Both"
        rowSelected={handleRowSelect}
        cssClass="workflows-grid"
      >
        <ColumnsDirective>
          <ColumnDirective field="name" headerText="Name" width="250" textAlign="Left" allowSorting={true} />
          <ColumnDirective field="trigger" headerText="Trigger" width="180" textAlign="Left" allowSorting={true} template={triggerTemplate} />
          <ColumnDirective field="isActive" headerText="Status" width="120" textAlign="Center" allowSorting={true} template={statusTemplate} />
          <ColumnDirective field="stepCount" headerText="Steps" width="100" textAlign="Center" allowSorting={true} />
          <ColumnDirective field="conditionCount" headerText="Conditions" width="120" textAlign="Center" allowSorting={true} />
          <ColumnDirective field="createdDate" headerText="Created" width="120" textAlign="Left" allowSorting={true} template={dateTemplate} />
          <ColumnDirective headerText="Actions" width="150" textAlign="Center" allowSorting={false} template={actionsTemplate} />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
};

export default WorkflowsGrid;
